import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Portal,
    Tooltip,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import ClientTransferRequestService from '../../Services/clientTransferRequest.service.js';
import styles from './RequestTransferModal.module.scss';

const RequestTransferModal = ({
    open,
    onClose,
    caseId,
    token,
    clientDetails = {},
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    const [step, setStep] = useState('form'); // form | confirm | success | error
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [accountantName, setAccountantName] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!open) return undefined;
        setStep('form');
        setBusy(false);
        setError('');
        setAccountantName('');
        setDescription('');
        return undefined;
    }, [open, caseId]);

    if (!open) return null;

    const detailRows = [
        { label: t('RequestTransferDetailCaseId') || 'Case ID', value: clientDetails.caseId || caseId },
        { label: t('RequestTransferDetailName') || 'Name', value: clientDetails.name },
        { label: t('RequestTransferDetailPhone') || 'Phone', value: clientDetails.phone },
        { label: t('RequestTransferDetailEmail') || 'Email', value: clientDetails.email },
        { label: t('RequestTransferDetailCaseType') || 'Case type', value: clientDetails.caseType },
        { label: t('RequestTransferDetailReportingType') || 'Reporting type', value: clientDetails.reportingType },
    ].filter((row) => row.value != null && row.value !== '');

    const buildNotes = () => {
        const parts = [
            accountantName.trim()
                ? `${t('RequestTransferNotesAccountantPrefix')}: ${accountantName.trim()}`
                : '',
            description.trim(),
        ].filter(Boolean);
        return parts.join('. ') || null;
    };

    const handleSend = () => {
        setError('');
        setStep('confirm');
    };

    const handleConfirm = async () => {
        if (!token || !caseId) return;
        setBusy(true);
        setError('');
        try {
            const createResult = await ClientTransferRequestService.Create(token, {
                caseId,
                requestedToAccountantId: null,
                notes: buildNotes(),
            });
            if (createResult.success) {
                setStep('success');
            } else {
                setError(createResult.error || t('RequestTransferFailed'));
                setStep('error');
            }
        } catch (e) {
            setError(e?.message || t('RequestTransferFailed'));
            setStep('error');
        } finally {
            setBusy(false);
        }
    };

    const title =
        step === 'success'
            ? t('RequestTransferSuccess')
            : step === 'confirm'
                ? t('RequestTransferConfirmTitle')
                : step === 'error'
                    ? t('RequestTransferFailed')
                    : t('RequestTransfer');

    return (
        <Portal>
            <div className={styles.overlay} onClick={onClose}>
                <div dir={dir} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.scroll}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{title}</div>
                                <div />
                            </div>

                            <div className={styles.body}>
                                {step === 'form' && (
                                    <>
                                        <div className={styles.alertInfo}>
                                            <InfoOutlinedIcon fontSize="small" />
                                            <span>{t('RequestTransferClientWillMoveMessage')}</span>
                                        </div>

                                        <div className={styles.detailsBox}>
                                            {detailRows.length > 0 ? (
                                                detailRows.map((row) => (
                                                    <div key={row.label} className={styles.detailRow}>
                                                        <strong>{row.label}:</strong>
                                                        <span>{row.value}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <span>{t('RequestTransferNoDetails') || 'No details'}</span>
                                            )}
                                        </div>

                                        <label className={styles.field}>
                                            <span className={styles.fieldLabel}>
                                                {t('RequestTransferAccountantName')}
                                            </span>
                                            <input
                                                className={styles.input}
                                                type="text"
                                                value={accountantName}
                                                onChange={(e) => setAccountantName(e.target.value)}
                                                placeholder={t('RequestTransferAccountantOptional')}
                                                disabled={busy}
                                            />
                                        </label>

                                        <label className={styles.field}>
                                            <span className={styles.fieldLabel}>
                                                {t('RequestTransferDescription')}
                                            </span>
                                            <textarea
                                                className={styles.textarea}
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder={t('RequestTransferDescriptionOptional')}
                                                disabled={busy}
                                                rows={4}
                                            />
                                        </label>
                                    </>
                                )}

                                {step === 'confirm' && (
                                    <div className={styles.alertInfo}>
                                        <InfoOutlinedIcon fontSize="small" />
                                        <span>{t('RequestTransferConfirmMessage')}</span>
                                    </div>
                                )}

                                {step === 'success' && (
                                    <div className={styles.alertSuccess}>
                                        <span>{t('RequestTransferSuccess')}</span>
                                    </div>
                                )}

                                {step === 'error' && (
                                    <div className={styles.alertError}>
                                        <ErrorIcon fontSize="small" />
                                        <span>{error || t('RequestTransferFailed')}</span>
                                    </div>
                                )}
                            </div>

                            <div className={styles.footer}>
                                {step === 'form' && (
                                    <>
                                        <button type="button" className={styles.cancel} onClick={onClose}>
                                            {t('Cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.save}
                                            onClick={handleSend}
                                            disabled={busy || !caseId}
                                        >
                                            {t('RequestTransferSend')}
                                        </button>
                                    </>
                                )}

                                {step === 'confirm' && (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.cancel}
                                            onClick={() => setStep('form')}
                                            disabled={busy}
                                        >
                                            {t('Cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.save}
                                            onClick={handleConfirm}
                                            disabled={busy}
                                        >
                                            {busy ? (
                                                <CircularProgress size={20} sx={{ color: 'var(--xtram-white)' }} />
                                            ) : null}
                                            {t('Yes')}
                                        </button>
                                    </>
                                )}

                                {(step === 'success' || step === 'error') && (
                                    <button type="button" className={styles.save} onClick={onClose}>
                                        {t('Close')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

RequestTransferModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    caseId: PropTypes.string,
    token: PropTypes.string,
    clientDetails: PropTypes.shape({
        caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        caseType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        reportingType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

export default RequestTransferModal;
